import { Permissions } from './../../../../../core/constants/permissions';
import { Component, ElementRef, HostListener, Inject, OnInit, ViewChild } from '@angular/core';
import { CallResource, PatientTrip, PatientVisit } from './patient-visit-detail.model';
import { ActivatedRoute, Router } from '@angular/router';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { DOCUMENT } from '@angular/common';
import { DateHelper } from "../../../../../core/helpers/date-helper";
import { PatientDetail } from "../../../../../core/models/patient-detail.model";
import { PatientService } from "../../../../../core/services/patient.service";
import { LogHelper } from "../../../../../core/helpers/log.helper";
import { AuthService } from "../../../../../core/services/auth.service";
import { AlertService } from "../../../../../shared/alert/alert.service";
import { TemplateService } from "../../../../../core/services/template.service";
import { ModalComponent } from "../../../../../shared/modal/modal.component";
import { StringHelper } from "../../../../../core/helpers/string-helper";
import { EditVisitModalComponent } from "../../../../visit/edit-visit-modal/edit-visit-modal.component";
import { AddEditTripModalComponent } from "../../../../visit/add-edit-trip-modal/add-edit-trip-modal.component";
import { TripBookingStatus } from "../../../../../core/constants/trip-booking-status";
import { TravelRequestService } from "../../../../../core/services/travel-request.service";
import { PatientTripDirection } from "../../../../../core/models/patient-trip.model";
import { VisitService } from "../../../../../core/services/visit.service";
import { VideoPlayerModalComponent } from "../../../../../shared/video-player-modal/video-player-modal.component";
import { environment } from "../../../../../../environments/environment";

@Component({
  selector: 'app-patient-visit-detail',
  templateUrl: './patient-visit-detail.component.html',
  styleUrls: ['./patient-visit-detail.component.scss']
})
export class PatientVisitDetailComponent implements OnInit {
  @ViewChild('redDetailsBar') redDetailsBar: ElementRef;
  @ViewChild('deleteTripModal') deleteTripModal: ModalComponent;
  @ViewChild('deleteVisitModal') deleteVisitModal: ModalComponent;
  @ViewChild('editVisitModal') editVisitModal: EditVisitModalComponent;
  @ViewChild('addEditTripModal') addEditTripModal: AddEditTripModalComponent;
  @ViewChild('playbackModal') playbackModal: ModalComponent;
  @ViewChild('videoPlaybackModal') videoPlaybackModal: VideoPlayerModalComponent;

  patientTrialId: string;
  patient = new PatientDetail();
  visit = new PatientVisit();
  draggingAtIndex: number;
  isDraggingTrip = false;
  deleteTrip: PatientTrip;
  isDeleteProcessing = false;
  isDeleteVisitProcessing = false;
  timeMinutesToStr = DateHelper.minutesToTimeStr;
  stringHelper = StringHelper;
  Permissions = Permissions;
  maxTripRefreshAttempts = 20;
  tripRefreshAttempts = 0;
  refreshTripsInterval = 5000;
  creatingPrePaymentExpenseClaim: boolean = false;
  isProduction: boolean = environment.production;
  environmentName: string = environment.environment;

  PatientTripDirection = PatientTripDirection;
  callResources: CallResource[] = [];
  playbackUrl: string = null;
  playbackTitle: string = '';
  playbackType: string = '';

  /** ID of the recording currently being downloaded. Prevents the user from
   *  triggering multiple parallel downloads / duplicate modal opens by
   *  repeatedly clicking the play button while the blob is in flight. */
  loadingRecordingId: string | null = null;

  get showCallInfo(): boolean {
    return this.visit.virtualCallsEnabled
      && this.visit.type === 'Virtual'
      && (this.visit.virtualCallType === 'Audio' || this.visit.virtualCallType === 'Video');
  }

  get callTypeLabel(): string {
    if (!this.visit.virtualCallType) return 'Call';
    return this.visit.virtualCallType === 'Audio' ? 'Audio Call' : 'Video Call';
  }

  get consentStatus(): string {
    if (!this.visit.recordingAskForConsent) {
      return 'Consent given during ICF onboarding';
    }
    if (this.visit.patientRecordingConsentDateUtc) {
      return this.visit.patientRecordingConsent
        ? 'Patient consented to recording'
        : 'Patient declined recording';
    }
    return 'Consent not yet recorded';
  }

  get showConsentTimestamp(): boolean {
    return this.visit.recordingAskForConsent && !!this.visit.patientRecordingConsentDateUtc;
  }

  get consentIconClass(): string {
    if (!this.visit.recordingAskForConsent) {
      return 'call-info-panel__consent-icon--yes';
    }
    if (this.visit.patientRecordingConsent) {
      return 'call-info-panel__consent-icon--yes';
    }
    if (this.visit.patientRecordingConsentDateUtc) {
      return 'call-info-panel__consent-icon--no';
    }
    return 'call-info-panel__consent-icon--pending';
  }

  private buildCallResources(): void {
    const resources: CallResource[] = [];
    for (const cr of this.visit.callRecordings) {
      const type = cr.recordingType === 'Video' ? 'Video' : 'Audio';
      const status = cr.status === 'Completed' ? 'Ready' : cr.status === 'Failed' ? 'Failed' : 'Pending';
      const timestamp = new Date(cr.createdUtc).toLocaleString([], { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
      const createdDisplay = this.formatInSiteTimezone(cr.createdUtc);

      resources.push({ id: cr.id, type, status, createdUtc: cr.createdUtc, createdDisplay, name: `${type} Recording ${timestamp}` });

      if (this.visit.virtualCallTranscriptEnabled) {
        const tStatus = cr.transcriptionStatus === 'Completed' ? 'Ready' : cr.transcriptionStatus === 'Failed' ? 'Failed' : 'Pending';
        resources.push({ id: `${cr.id}-transcript`, type: 'Transcript', status: tStatus, createdUtc: cr.createdUtc, createdDisplay, name: `Transcript ${timestamp}` });
      }
    }
    this.callResources = resources;
  }

  private formatInSiteTimezone(utc: string): string {
    const date = new Date(utc);
    const tz = this.visit.siteTimezone;
    const options: Intl.DateTimeFormatOptions = {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: false,
      timeZone: tz || 'UTC', timeZoneName: 'short',
    };
    try {
      return new Intl.DateTimeFormat('en-GB', options).format(date);
    } catch {
      return new Intl.DateTimeFormat('en-GB', { ...options, timeZone: 'UTC' }).format(date);
    }
  }

  @HostListener('window:scroll', ['$event']) // for window scroll events
  onScroll(event) {
    const redBarContainer = document.getElementById('red-bar-container');
    const redBarElement = this.redDetailsBar.nativeElement;
    const headerHeightAndOffset = 15;
    const containerTopPos = redBarContainer.getBoundingClientRect().top - headerHeightAndOffset;

    if (containerTopPos <= 0 && !redBarElement.classList.contains('fixed')) {
      redBarElement.style.width = redBarElement.offsetWidth + 'px';
      redBarElement.classList.add('fixed');
      redBarElement.style.top = headerHeightAndOffset + 'px';
      redBarContainer.style.height = redBarElement.offsetHeight + 30 + 'px';
    } else if (containerTopPos > 0 && redBarElement.classList.contains('fixed')) {
      redBarElement.classList.remove('fixed');
      redBarElement.style.width = null;
      redBarElement.style.top = null;
      redBarContainer.style.height = null;
    }
  }

  constructor(@Inject(DOCUMENT) private document: Document, public authService: AuthService,
    private readonly visitService: VisitService,
    private readonly patientService: PatientService, private readonly alertService: AlertService,
    private readonly activatedRoute: ActivatedRoute, private readonly templateService: TemplateService,
    private readonly router: Router, private readonly travelRequestService: TravelRequestService) { }

  ngOnInit() {
    this.templateService.showHeader();

    this.loadVisit();
  }

  loadRefreshTrips() {
    if (this.tripRefreshAttempts < this.maxTripRefreshAttempts) {
      this.tripRefreshAttempts++;
            setTimeout(() => {
              this.loadVisit();
            }, this.refreshTripsInterval);
    }
  }

  loadVisit(): void {
    this.activatedRoute.params.subscribe(params => {
      this.patientTrialId = params.id;
      this.patientService.retrieveVisit(params.visitId).subscribe({
        next: visit => {
          this.visit = visit;
          this.buildCallResources();

          // If there are PDF's in the results that are still generating, refresh the trips every 5 seconds
          const tripsGeneratingPdfs = this.visit.trips.filter(trip => trip.pdfGenerating);
          if (tripsGeneratingPdfs.length > 0)
            this.loadRefreshTrips();
        },
        error: error => {
          LogHelper.log(error);
        }
      });

      this.patientService.retrievePatientDetailFromPatientTrialId(params.id).subscribe({
        next: patient => {
          this.patient = patient;
        },
        error: error => {
          this.alertService.showWarningAlert('Unable to load patient information!');
        }
      });
    });
  }

  onCopiedToClipboard(text: string) {
    this.alertService.showSuccessAlert(text);
  }

  getPreloadPaymentState() {
    if (this.visit.preloadPaymentDate) {
      return "Pending";
    } else if (this.visit.preloadExpenseCreated) {
      return "Created";
    }

    return null;
  }

  onDeleteTrip(trip: PatientTrip) {
    this.deleteTrip = trip;
    this.deleteTripModal.show();
  }

  onConfirmDeleteTrip() {
    this.isDeleteProcessing = true;
    this.alertService.clearAll();
    this.patientService.deleteTrip(this.deleteTrip.id).subscribe({
      next: () => {
        const index: number = this.visit.trips.indexOf(this.deleteTrip);
        if (index !== -1) {
          this.visit.trips.splice(index, 1);
        }

        this.deleteTripModal.hide();
        this.alertService.showSuccessAlert('Trip successfully removed');
        this.isDeleteProcessing = false;
      },
      error: error => {
        LogHelper.log(error);
        this.alertService.showWarningAlert('There was a problem removing the trip!');
        this.deleteTripModal.hide();
        this.isDeleteProcessing = false;
      }
    });
  }

  /**
   * Called when the user selects to edit the visit
   */
  onHandleEditVisit(): void {
    this.editVisitModal.show(this.patientTrialId, this.visit.id);
  }

  /**
   * Called when a new trip is added via the trip modal
   */
  onHandleTripAdded(data: {visitId: string, addMuvRides: boolean}): void {
    this.loadVisit();

    if (data.addMuvRides) {
      this.addEditTripModal.showEditTripModal(data.visitId, 'MUV Rides');
    }
  }

  /**
   * When dragging an item starts, store it's current index so we can refer to it later
   * @param index
   */
  onDrag(index: number) {
    this.draggingAtIndex = index;
    this.isDraggingTrip = true;
  }

  onDeleteVisitConfirmed() {
    this.isDeleteVisitProcessing = true;
    this.patientService.deleteVisit(this.visit.id).subscribe({
      next: () => {
        this.alertService.showSuccessAlert('Visit Successfully Delete.');
        this.router.navigate(['/patient', this.patientTrialId]).then();
        this.deleteVisitModal.hide();
      },
      error: () => {
        this.isDeleteVisitProcessing = false;
        this.deleteVisitModal.hide();
        this.alertService.showWarningAlert('There was a problem deleting the visit.');
      }
    });
  }

  /**
   * Re-order the trips array when a trip has been dropped into a new position
   * @param event
   */
  onDrop(event: CdkDragDrop<string[]>) {
    this.isDraggingTrip = false;
    moveItemInArray(this.visit.trips, this.draggingAtIndex, event.currentIndex);
    setTimeout(this.reOrderTrips, 1000); // Wait a second before persisting
    this.reOrderTrips();
  }

  reOrderTrips() {
    if (!this.isDraggingTrip) { // Prevent making an API call if the user is dragging another item
      const tripOrder: { tripId: string; order: number }[] = [];
      let i = 0;
      for (const trip of this.visit.trips) {
        tripOrder.push({ tripId: trip.id, order: i });
        i++;
      }
      this.patientService.reOrderTrips(tripOrder).subscribe({
        error: error => {
          LogHelper.log(error);
          this.alertService.showWarningAlert('Unable to re-order to trips!');
        }
      });
    }
  }

  getTripBookingStatusDescription(status: string): string {
    return TripBookingStatus.getDescription(status);
  }

  getDepartureLabel(type: string) {
    switch (type) {
      case 'Accommodation':
        return 'Check-in';
      case 'Rental Car':
        return 'Pick-up';
    }

    return 'Departure';
  }

  getArrivalLabel(type: string) {
    switch (type) {
      case 'Accommodation':
        return 'Check-out';
      case 'Rental Car':
        return 'Drop-off';
    }

    return 'Arrival';
  }

  onCreatePrePaymentExpenseClaim() {
    if (this.isProduction)
      return;

    this.creatingPrePaymentExpenseClaim = true;

    this.visitService.createPrePaymentExpenseClaim(this.visit.id).subscribe({
      next: () => {
        this.creatingPrePaymentExpenseClaim = false;
        this.alertService.showSuccessAlert('Pre-payment expense claim successfully created.');
        this.visit.preloadExpenseCreated = true;
      },
      error: err => {
        this.alertService.showWarningAlert('There was a problem creating the pre-payment expense claim.')
        this.alertService.showErrorAlert(err);
        this.creatingPrePaymentExpenseClaim = false;
      }
    });
  }

  onPlayResource(resource: CallResource): void {
    // Re-entry guard — a download is already in flight, so do nothing.
    // Combined with the row-action disabled state this stops users mashing
    // the button and getting multiple parallel downloads / modal popups.
    if (this.loadingRecordingId !== null) return;

    const recordingId = resource.id;
    this.loadingRecordingId = recordingId;

    // Open the modal immediately so the user sees instant feedback —
    // both modals render a spinner while the URL/src is empty.
    if (resource.type === 'Video') {
      this.videoPlaybackModal.openLoading(resource.name);
    } else {
      if (this.playbackUrl) {
        URL.revokeObjectURL(this.playbackUrl);
      }
      this.playbackUrl = null;
      this.playbackTitle = resource.name;
      this.playbackModal.show();
    }

    this.visitService.downloadRecording(this.visit.id, recordingId).subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);

        if (resource.type === 'Video') {
          this.videoPlaybackModal.setSource(url);
        } else {
          this.playbackUrl = url;
        }
        this.loadingRecordingId = null;
      },
      error: () => {
        if (resource.type === 'Video') {
          this.videoPlaybackModal.close();
        } else {
          this.playbackModal.hide();
          this.playbackUrl = null;
        }
        this.loadingRecordingId = null;
        this.alertService.showWarningAlert('Failed to load recording.');
      }
    });
  }

  onDownloadTranscript(resource: CallResource): void {
    const recordingId = resource.id.replace('-transcript', '');
    this.visitService.downloadTranscript(this.visit.id, recordingId).subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${resource.name}.txt`;
        a.click();
        URL.revokeObjectURL(url);
      },
      error: () => {
        this.alertService.showWarningAlert('Failed to download transcript.');
      }
    });
  }

  onClosePlayback(): void {
    this.playbackModal.hide();
    if (this.playbackUrl) {
      URL.revokeObjectURL(this.playbackUrl);
      this.playbackUrl = null;
    }
  }

}
